import UserContext from "Context/UserProvider";
import { useContext, useEffect, useState } from "react";
import BusinessLocationsTable from "../../components/business/InfrastructureComponent/BusinessLocationsTable";
import VirtualChargingHubsTable from "../../components/business/InfrastructureComponent/VirtualChargingHubsTable";
import HomeLocationsTable from "../../components/business/InfrastructureComponent/HomeLocationsTable";
import OpportunityLocationsTable from "../../components/business/InfrastructureComponent/OpportunityLocationsTable";
import { Box, Tab, Tabs, CircularProgress, Typography } from '@mui/material';
import { useApiService } from "../../services/ApiService/ApiService";
import toast from 'react-hot-toast';

function isNull(array: any) {
    if (array === null || array === undefined) {
        return [];
    }
    return array;
}

function addIdsToLocations(locations: any[]) {
    return locations.map((location, index) => ({
        ...location,
        id: location.uuid || `location-${index}` // Use uuid if available, otherwise generate a fallback id
    }));
}

function InfrastructurePage(props: any) {
    const { userInfo } = useContext(UserContext);
    const { fetchWithAuth } = useApiService();
    const [businessLocations, setBusinessLocations] = useState([]) as any;
    const [virtualChargingHubs, setVirtualChargingHubs] = useState([]) as any;
    const [homeLocations, setHomeLocations] = useState([]) as any;
    const [opportunityLocations, setOpportunityLocations] = useState([]) as any;
    const [loading, setLoading] = useState(true);
    const [isError, setIsError] = useState(false);
    const [activeTab, setActiveTab] = useState(0);

    const fetchLocations = async () => {
        try {
            const response = await fetchWithAuth('/infrastructure/dwelling/list', {
                method: 'POST',
                body: JSON.stringify({
                    uuid: userInfo.uuid,
                    businessUuid: userInfo.businessUuid,
                    fleetUuid: userInfo.fleetUuid
                })
            });

            if (!response.ok) {
                throw new Error('Failed to fetch infrastructure data');
            }

            const res_json = await response.json();
            setBusinessLocations(addIdsToLocations(isNull(res_json.businessLocations)));
            setVirtualChargingHubs(addIdsToLocations(isNull(res_json.virtualChargingHubs)));
            setHomeLocations(addIdsToLocations(isNull(res_json.homeLocations)));
            setOpportunityLocations(addIdsToLocations(isNull(res_json.opportunityLocations)));
            setIsError(false);
        } catch (error) {
            console.error('Error fetching infrastructure data:', error);
            setIsError(true);
            toast.error('Failed to fetch infrastructure data');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchLocations();
    }, []);

    const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
        setActiveTab(newValue);
    };

    if (loading) {
        return (
            <Box 
                component="section" 
                sx={{ p: 3 }}
            >
                <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
                    <CircularProgress />
                </Box>
            </Box>
        );
    }

    if (isError) {
        return (
            <Box 
                component="section" 
                sx={{ p: 3 }}
            >
                <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
                    <Typography color="error">Failed to load infrastructure data</Typography>
                </Box>
            </Box>
        );
    }

    return (
        <Box 
            component="section"
            sx={{ p: 3 }}
        >
            <Box sx={{ width: '100%' }}>
                <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
                    <Tabs 
                        value={activeTab} 
                        onChange={handleTabChange}
                        aria-label="infrastructure tabs"
                    >
                        <Tab label="Business Locations" />
                        <Tab label="Virtual Charging Hubs" />
                        <Tab label="Home Locations" />
                        <Tab label="Opportunity Locations" />
                    </Tabs>
                </Box>

                <Box>
                    {activeTab === 0 && (
                        <BusinessLocationsTable data={businessLocations} />
                    )}
                    {activeTab === 1 && (
                        <VirtualChargingHubsTable data={virtualChargingHubs} />
                    )}
                    {activeTab === 2 && (
                        <HomeLocationsTable data={homeLocations} />
                    )}
                    {activeTab === 3 && (
                        <OpportunityLocationsTable data={opportunityLocations} />
                    )}
                </Box>
            </Box>
        </Box>
    );
}

export default InfrastructurePage;
